import React from 'react'
import { enableMapSet, produce } from 'immer'
import { Provider, useDispatch, useSelector } from 'react-redux'
import { createStore } from 'redux'
import mainStore from '../../../../store'
import { useMainStoreSync } from '../../../../common/hooks/useMainStoreSync'
import { getCurrentFiscalYear } from './helpers'
import _set from 'lodash/set'
import {
  assertSecuritiesReportResponseSchema,
  assetSecuritiesReportStateSchema,
  convertRCompanyConfigToS,
  convertRGroupCompanyToS
} from './types'

enableMapSet()

const appState = mainStore.getState

const initialState = assetSecuritiesReportStateSchema({
  mainStore: appState(),
  selectedFiscalYear: '',
  pageState: {},
  yearList: [],
  groupCompanyList: [],
  master: {
    Classification: 'dashboard'
  },
  errors: {
    server: [],
    client: {}
  }
})

export const securitiesReportReducer = (state = initialState, action) => {
  return produce(state, (draft) => {
    const { payload, type } = action

    const setPageState = (selectedFiscalYear, companyConfig, groupCompany) => {
      _set(draft.pageState, [selectedFiscalYear, 'companyConfig'], companyConfig)
      _set(draft.pageState, [selectedFiscalYear, 'groupCompany'], groupCompany)
    }

    switch (type) {
      case 'UPDATE_FROM_MAIN': {
        draft.mainStore = payload
        break
      }
      case 'INIT': {
        const accPeriod = draft.mainStore.company.accPeriod
        const currentFiscalYear = getCurrentFiscalYear(accPeriod, true)
        const securitiesConfig = assertSecuritiesReportResponseSchema(payload.securitiesConfig)

        draft.master = payload.master
        draft.yearList = payload.yearList
        draft.groupCompanyList = payload.groupCompanyList

        draft.selectedFiscalYear = currentFiscalYear

        draft.pageState = {}

        setPageState(
          currentFiscalYear,
          convertRCompanyConfigToS(securitiesConfig),
          convertRGroupCompanyToS(securitiesConfig)
        )

        break
      }
      case 'CHANGE_FISCAL_YEAR': {
        const { selectedFiscalYear, securitiesConfig } = payload

        // 既に画面状態がある場合は年度切替のみ
        if (draft.pageState[selectedFiscalYear]) {
          draft.selectedFiscalYear = selectedFiscalYear
          break
        }

        // 取得失敗時は選択を維持（Immer 例外でロールバックさせない）
        if (!securitiesConfig) break

        // INIT と同様、レスポンスを正規化してから状態へ変換
        // （include_in_submitting_company_status 未返却時の Zod 例外を防ぐ）
        const parsed = assertSecuritiesReportResponseSchema(securitiesConfig)

        draft.selectedFiscalYear = selectedFiscalYear

        setPageState(selectedFiscalYear, convertRCompanyConfigToS(parsed), convertRGroupCompanyToS(parsed))

        draft.errors = {
          server: [],
          client: {}
        }

        break
      }

      case 'SET_COMPANY_CONFIG': {
        const { path, value } = payload

        const selectedFiscalYear = draft.selectedFiscalYear

        _set(draft.pageState, [selectedFiscalYear, 'companyConfig', path], value)
        break
      }

      case 'SET_GROUP_COMPANY': {
        const { path, value } = payload

        const selectedFiscalYear = draft.selectedFiscalYear

        const updatePath = [selectedFiscalYear, 'groupCompany'].concat(path)

        _set(draft.pageState, updatePath, value)
        break
      }

      case 'COPY_PREVIOUS_YEAR_SETTING': {
        break
      }

      case 'UPDATE_STATE': {
        _set(draft, payload.path, payload.value)
      }
      default:
        break
    }
  })
}

/**
 * Dispatchを変更
 *
 * @returns
 */
export const useSecuritiesReportDispatch = () => {
  const _dispatch = useDispatch()
  return (type, payload) => _dispatch({ type, payload })
}

/** @type {import('react-redux').TypedUseSelectorHook<typeof initialState>} */
export const useSecuritiesReportSelector = useSelector

/**
 * @function SecuritiesProviderStore プロバイダー
 * @param {any} props
 * @returns
 * @component
 */
export const SecuritiesProviderStore = (props) => {
  const [store] = React.useState(() => createStore(securitiesReportReducer, { ...initialState, mainStore: appState() }))
  useMainStoreSync(store)
  return <Provider store={store}>{props.children}</Provider>
}
